/**
 * @param divisions - variadic list of divisions where the value represents division size as a portion of 20.
 * Portion of 20 was chosen to give granularity of 5%.
 * @returns a function that accepts a seed value to allow us to shift around the divisions to avoid having groups made
 * up of the same users.
 */
const testGroupDivisions = <T extends number[]>(...divisions: T) => {
	// Sanity check
	if (divisions.reduce((acc, g) => acc + g) !== 20) throw new Error('AB Test group divisions must add up to 20')

	// Calculate start and end values for each division
	const boundedDivisions = expandDivisionSizesIntoBoundries(divisions)

	// Now we want a seed to avoid having groups made up of the same users.
	return (seed: number) => {
		// Return a function for each division that takes the player id
		// and returns true if the player is part of that division.
		return boundedDivisions.map(({ start, end }) => {
			return (playerId: number) => {
				const seededPosition = (playerId + seed) % 20
				return start <= seededPosition && seededPosition < end
			}
		}) as ExtractCallbackFromTuple<T>
	}
}

// This type ensures that if you pass in 3 divisions you get back 3 grouping functions (type safe).
type ExtractCallbackFromTuple<TUPLE> = { [K in keyof TUPLE]: (n: number) => boolean };

/**
 * Calculate start and end positions for each given divisions.
 */
const expandDivisionSizesIntoBoundries = (divisions: number[]): Array<{ start: number; end: number }> => {
	return divisions.reduce((accumulated, currentWidth) => {
		// Take the end value of the last division (or 0 if this is the first)
		const start = accumulated.length ? accumulated[accumulated.length - 1].end : 0
		const bounded = { start, end: start + currentWidth }
		accumulated.push(bounded)
		return accumulated
	}, [])
}

export const ABC_45_45_10 = testGroupDivisions(9, 9, 2)
export const AB_90_10 = testGroupDivisions(18, 2)
export const AB_5_95 = testGroupDivisions(1, 19)
export const AB_10_90 = testGroupDivisions(2, 18)
export const AB_20_80 = testGroupDivisions(4, 16)
export const AB_25_75 = testGroupDivisions(5, 15)
export const AB_50_50 = testGroupDivisions(10, 10)
export const ABC_25_25_50 = testGroupDivisions(5, 5, 10)
